import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import QrCodeIcon from '@mui/icons-material/QrCode';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import StoreIcon from '@mui/icons-material/Store';
import { toast } from 'react-toastify';
import api from '../services/api';
import { trackTikTokCompletePayment } from '../components/TikTokPixel';
import { useTracking } from '../context/TrackingContext';

const OrderComplete = () => {
  const { orderNo } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { trackPurchase } = useTracking();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const paymentInfo = location.state?.paymentInfo;

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await api.get(`/orders/${orderNo}`);
        const data = response.data;
        setOrder(data);

        const trackedKey = `order_tracked_${orderNo}`;
        if (!sessionStorage.getItem(trackedKey)) {
          sessionStorage.setItem(trackedKey, 'true');

          trackTikTokCompletePayment(data);
          trackPurchase({
            orderNo: data.order_no,
            totalAmount: parseFloat(data.total_amount || 0),
            items: (data.items || []).map(item => ({
              item_id: item.product_sku || item.sku,
              item_name: item.product_name || item.name,
              price: item.price,
              quantity: item.quantity
            }))
          });
        }
      } catch (error) {
        console.error('Failed to fetch order:', error);
        toast.error('訂單不存在或已失效');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderNo, trackPurchase, navigate]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text || '');
    toast.success('已複製');
  };

  const getPaymentMethodName = (method) => {
    const names = {
      credit_card: '信用卡',
      credit: '信用卡',
      atm: 'ATM 轉帳',
      cvs_barcode: '超商條碼',
      cvs: '超商條碼',
      smilepay: '超商條碼',
      cod: '貨到付款',
      c2c: '超商取貨付款',
      linepay: 'LINE Pay'
    };
    return names[method] || method || '-';
  };

  const renderATMInfo = () => {
    const atm = paymentInfo || {
      bankName: order?.atm_bank_name,
      bankNo: order?.atm_bank_no,
      accountNo: order?.atm_no,
      deadline: order?.pay_deadline || order?.payment_deadline
    };

    if (!atm.accountNo) return null;

    return (
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#e3f2fd' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AccountBalanceIcon color="primary" />
          <Typography variant="h6">ATM 轉帳資訊</Typography>
        </Box>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: 120 }}>銀行</TableCell>
              <TableCell>{atm.bankName || atm.bankNo || '-'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>帳號</TableCell>
              <TableCell>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{atm.accountNo}</Typography>
                <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copyToClipboard(atm.accountNo)}>
                  複製帳號
                </Button>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>轉帳金額</TableCell>
              <TableCell><Typography color="error" fontWeight="bold">NT$ {Number(order?.total_amount || 0).toLocaleString()}</Typography></TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>期限</TableCell>
              <TableCell>{atm.deadline ? new Date(atm.deadline).toLocaleString('zh-TW') : '-'}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    );
  };

  const renderCVSInfo = () => {
    const cvs = paymentInfo || {
      barcode1: order?.barcode1,
      barcode2: order?.barcode2,
      barcode3: order?.barcode3,
      deadline: order?.pay_deadline || order?.payment_deadline
    };

    if (!cvs.barcode1 && !cvs.barcode2 && !cvs.barcode3 && !order?.payment_code) return null;

    return (
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#fff3e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <QrCodeIcon color="warning" />
          <Typography variant="h6">超商付款資訊</Typography>
        </Box>
        {[cvs.barcode1, cvs.barcode2, cvs.barcode3, order?.payment_code].filter(Boolean).map((code, idx) => (
          <Box key={`${code}-${idx}`} sx={{ p: 1.5, mb: 1, bgcolor: 'white', borderRadius: 1 }}>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{code}</Typography>
          </Box>
        ))}
        <Typography variant="body2">期限：{cvs.deadline ? new Date(cvs.deadline).toLocaleString('zh-TW') : '-'}</Typography>
      </Paper>
    );
  };

  const renderCODInfo = () => {
    if (!['cod', 'c2c'].includes(order?.payment_method)) return null;
    return (
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#e8f5e9' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LocalAtmIcon color="success" />
          <Typography variant="h6">貨到付款</Typography>
        </Box>
        <Typography>取貨時支付 NT$ {Number(order?.total_amount || 0).toLocaleString()}。</Typography>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box>
        <Skeleton height={60} width="50%" />
        <Skeleton height={180} />
        <Skeleton height={140} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant="h5" fontWeight="bold">訂單建立成功</Typography>
        <Typography color="text.secondary">請保留訂單資訊以便後續查詢</Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>訂單資訊</Typography>
        <Table size="small">
          <TableBody>
            <TableRow><TableCell sx={{ fontWeight: 'bold', width: 120 }}>訂單編號</TableCell><TableCell>{order?.order_no} <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copyToClipboard(order?.order_no)}>複製</Button></TableCell></TableRow>
            <TableRow><TableCell sx={{ fontWeight: 'bold' }}>付款方式</TableCell><TableCell>{getPaymentMethodName(order?.payment_method)}</TableCell></TableRow>
            <TableRow><TableCell sx={{ fontWeight: 'bold' }}>付款狀態</TableCell><TableCell><Chip size="small" color={order?.payment_status === 'paid' ? 'success' : 'warning'} label={order?.payment_status === 'paid' ? '已付款' : '待付款'} /></TableCell></TableRow>
            <TableRow><TableCell sx={{ fontWeight: 'bold' }}>訂單金額</TableCell><TableCell>NT$ {Number(order?.total_amount || 0).toLocaleString()}</TableCell></TableRow>
          </TableBody>
        </Table>
      </Paper>

      {order?.payment_status !== 'paid' && (() => {
        const method = order?.payment_method;
        if (method === 'atm') return renderATMInfo();
        if (method === 'credit' || method === 'credit_card') return (
          <Alert severity="info" sx={{ mb: 3 }}>信用卡付款處理中，付款完成後系統將自動更新訂單狀態</Alert>
        );
        return renderCVSInfo();
      })()}
      {renderCODInfo()}

      {order?.store_name && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <StoreIcon color="primary" />
            <Typography variant="h6">取貨資訊</Typography>
          </Box>
          <Typography>{order.store_type} - {order.store_name}</Typography>
          <Typography color="text.secondary">{order.store_address}</Typography>
        </Paper>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>商品明細</Typography>
        {(order?.items || []).map((item, idx) => (
          <Box key={`${item.sku || item.product_id}-${idx}`} sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
            <Typography>{item.product_name || item.name} × {item.quantity}</Typography>
            <Typography>NT$ {Number(item.subtotal || item.price * item.quantity || 0).toLocaleString()}</Typography>
          </Box>
        ))}
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6">總計：NT$ {Number(order?.total_amount || 0).toLocaleString()}</Typography>
      </Paper>

      <Alert severity="info" sx={{ mb: 3 }}>如需查詢訂單狀態，請保留訂單編號。</Alert>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button variant="outlined" onClick={() => navigate(`/order/${order?.order_no}`)}>查詢訂單狀態</Button>
        <Button variant="contained" onClick={() => navigate('/')}>返回首頁</Button>
      </Box>
    </Box>
  );
};

export default OrderComplete;
